import React, {FunctionComponent, useMemo, useEffect, useState} from "react";
import { useProvidedExercise, ExerciseProvider } from './exercise-provider';
import ExerciseSelector from "./exercise-selector";
import Keyboard from '../../components/keyboard';
import { Mode, PitchClassName, Range } from '../../lib/music/index';
import useHighlightedKeys from '../../components/keyboard/use-highlighted-keys';
import useKeyboard from './use-keyboard';
import { ScaleExercise } from '../../models/scale-exercise';
import { ModeName } from '../../models/mode';
import { RangeName } from "../../models/range";
import useFingeringAnnotations from '../../components/keyboard/use-fingering-annotations';
import AnnotationEditor from './annotation-editor';
import { useQueryExercises } from "../../services/graphql/use-exercises";
import Header from "../../blocks/header";
import { useAuth } from '../../services/auth/auth-provider';
import Footer from "../../blocks/footer";
import HandToggles from "./hand-toggles";

const DEFAULTS = {
  height: 300,
  range: 41,
  startKeyIndex: 15,
  width: 1280,
};

interface ScalesExerciseProps {
  height: number;
  range: number;
  startKeyIndex: number;
  width: number;
}

const BASE_URL = `${window.location.protocol}//${window.location.host}`;

const ScalesExercise: FunctionComponent<ScalesExerciseProps> = (props) => {
  const vm = useVM(props);

  return (<>
    <div className='container flex flex-1 flex-col mx-auto'>
      <Header />
      
      <ExerciseSelector exercise={vm.exercise} updateExercise={vm.updateExercise} />

      { vm.isEditor && <AnnotationEditor exercise={vm.exercise} updateExercise={vm.updateExercise} /> }

      <HandToggles
        setShowLeftHand={vm.setShowLeftHand}
        setShowRightHand={vm.setShowRightHand}
        showLeftHand={vm.showLeftHand}
        showRightHand={vm.showRightHand}
      />

      <Keyboard
        className='mt-8'
        fingeringAnnotations={vm.fingeringAnnotations}
        height={vm.opts.height}
        highlightedKeys={vm.highlightedKeys}
        keys={vm.keyboard.keys}
        width={vm.opts.width}
      />

    </div>

    {
      vm.isEditor &&
      <div className='container flex flex-1 flex-col mx-auto mt-4'>
        <input readOnly={true} value={vm.exerciseURL} />
      </div>
    }

    <Footer />
  </>);
}

function useVM({height, range, startKeyIndex, width}: ScalesExerciseProps) {
  const {user} = useAuth();
  const {data: exercise, update: updateExercise} = useProvidedExercise();
  const {data} = useQueryExercises({where: {_and: [
    {pitch_class: {_eq: exercise.pitch_class}},
    {mode: {_eq: exercise.mode}},
    {range: {_eq: exercise.range}},
  ]}});

  const [showLeftHand, setShowLeftHand] = useState(true);
  const [showRightHand, setShowRightHand] = useState(true);

  const selectedPitchClass = useMemo(() => {
    return !exercise.pitch_class ?
      undefined :
      (exercise.pitch_class.replace('sharp', '#').toUpperCase()) as PitchClassName
  }, [exercise.pitch_class]);

  const isEditor = useMemo(() => {
    if (user && user["https://hasura.io/jwt/claims"] && user["https://hasura.io/jwt/claims"]['x-hasura-allowed-roles']) {
      return user["https://hasura.io/jwt/claims"]["x-hasura-allowed-roles"].includes('scale-editor');
    }

    return false;
  }, [user]);

  const keyboard = useKeyboard({height, range, startKeyIndex, width});

  const highlightedKeys = useHighlightedKeys(keyboard.keys, selectedPitchClass, mapMode(exercise.mode), mapRange(exercise.range));

  const {config: {left_hand = '', right_hand = ''} = {}} = exercise;

  const fingeringAnnotations = useFingeringAnnotations(highlightedKeys, showLeftHand ? left_hand : '', showRightHand ? right_hand : '');

  const exerciseURL = useMemo(() => {
    return `${BASE_URL}?pitch_class=${exercise.pitch_class}&mode=${exercise.mode}&range=${exercise.range}`;
  }, [exercise]);

  useEffect(() => {
    if (data) {
      const c: ScaleExercise['config'] = data.exercises[0] ? data.exercises[0].config : {left_hand: '', right_hand: ''};
      const id = data.exercises[0] ? data.exercises[0].id : undefined;
      updateExercise({
        ...exercise,
        id,
        config: {
          ...(exercise.config || {}),
          ...c
        }
      })
    }
  }, [data]);

  return {
    exercise,
    exerciseURL,
    fingeringAnnotations,
    highlightedKeys,
    isEditor,
    keyboard,
    opts: {height, width},
    setShowLeftHand,
    setShowRightHand,
    showLeftHand,
    showRightHand,
    updateExercise,
  }
}

const ScalesExerciseWithProviders: FunctionComponent = () => (
  <ExerciseProvider>
    <ScalesExercise {...DEFAULTS} />
  </ExerciseProvider>
);

export default ScalesExerciseWithProviders;

function mapMode(modelMode: ScaleExercise['mode']): Mode | undefined {
  switch (modelMode) {
    case ModeName.Major: return 'Major';
    case ModeName.HarmonicMinor: return 'Harmonic Minor';
    case ModeName.MelodicMinor: return 'Melodic Minor';
    case ModeName.NaturalMinor: return 'Natural Minor';
  }
}

function mapRange(modelRange: ScaleExercise['range']): Range | undefined {
  switch (modelRange) {
    case RangeName.Pentatonic: return 'Pentatonic';
    case RangeName.SingleOctave: return 'Octave';
    case RangeName.TwoOctaves: return 'Two octave';
  }
}
